/**
 * The KPI grid as a spreadsheet.
 *
 * Analysts were retyping the grid into Excel to do anything with it, so the
 * download hands them the SAME table they are looking at: whichever scope is
 * on screen (a property or the portfolio rollup), metrics in metric-pack
 * order, periods as columns in grid order.
 *
 * Two deliberate departures from the on-screen grid:
 *
 * - The group is a COLUMN, not a banner row. On screen a banner reads better
 *   because the eye tracks downward; in Excel a banner row is a hole in the
 *   data that breaks sort, filter and pivot — the three things someone opens
 *   the file to do.
 * - Cells carry NUMBERS, not the grid's formatted strings, with the unit
 *   expressed as an Excel number format. `$1,234` typed as text sums to zero.
 *   Percentages stay fractions (the storage form, see `formatKpiValue`) and
 *   the `0.0%` format renders them the way the grid does.
 *
 * The transform is pure and lives apart from the download so it can be tested
 * without a DOM or a workbook writer.
 */

import * as XLSX from "xlsx";

import type { KpiTable, KpiUnit } from "../../types/kpi";
import { formatPeriod } from "./formatKpiValue";

/** One metric row of the exported sheet. */
export interface KpiSheetRow {
	group: string;
	metric: string;
	unit: KpiUnit;
	/** Effective values, one per period, in `table.periods` order. */
	values: (number | null)[];
}

export interface KpiSheetData {
	/** `["Group", "Metric", ...period labels]`. */
	header: string[];
	rows: KpiSheetRow[];
}

/** Excel number formats, chosen to match how the grid renders each unit. */
const UNIT_FORMATS: Record<KpiUnit, string> = {
	usd: "$#,##0",
	count: "#,##0",
	pct: "0.0%",
	ratio: "0.00",
};

const SHEET_NAME = "KPIs";

/**
 * Flatten a loaded KPI table into header + metric rows.
 *
 * A cell with no effective value becomes `null` rather than a dash: an empty
 * spreadsheet cell is the honest rendering of "no number here", and a dash
 * would make the column text.
 */
export function kpiTableToSheetData(table: KpiTable): KpiSheetData {
	// Explicit lambda, not point-free: formatPeriod grew a `grain` second
	// parameter (PR #259), and map would feed it the array index.
	const header = [
		"Group",
		"Metric",
		...table.periods.map((period) => formatPeriod(period, table.period_grain)),
	];
	const rows: KpiSheetRow[] = [];

	for (const group of table.groups) {
		for (const metric of group.metrics) {
			rows.push({
				group: group.label,
				metric: metric.label,
				unit: metric.unit,
				values: table.periods.map((period) => {
					const value = metric.cells[period]?.effective_value;
					return typeof value === "number" && Number.isFinite(value)
						? value
						: null;
				}),
			});
		}
	}

	return { header, rows };
}

/** The scope's name as the file should carry it. */
export function kpiScopeLabel(table: KpiTable): string {
	return table.property_key ?? "Portfolio";
}

/**
 * `KPIs - <Portfolio|property key> - <YYYY-MM-DD>.xlsx`, with the characters
 * Excel and the filesystem reject stripped out of the property key.
 */
export function kpiSpreadsheetFilename(table: KpiTable, today: Date): string {
	const scope = kpiScopeLabel(table).replace(/[\\/:?*[\]]/g, "_").trim();
	const year = today.getFullYear();
	const month = String(today.getMonth() + 1).padStart(2, "0");
	const day = String(today.getDate()).padStart(2, "0");
	return `KPIs - ${scope} - ${year}-${month}-${day}.xlsx`;
}

/** Build the workbook. Exported for tests; the UI calls `downloadKpiTable`. */
export function buildKpiWorkbook(table: KpiTable): XLSX.WorkBook {
	const { header, rows } = kpiTableToSheetData(table);
	const aoa: (string | number | null)[][] = [
		header,
		...rows.map((row) => [row.group, row.metric, ...row.values]),
	];

	const worksheet = XLSX.utils.aoa_to_sheet(aoa);

	// Unit formats are per METRIC, so they are applied by row rather than by
	// column: one sheet mixes dollars, counts and rates.
	rows.forEach((row, rIdx) => {
		row.values.forEach((value, cIdx) => {
			if (value === null) return;
			const addr = XLSX.utils.encode_cell({ r: rIdx + 1, c: cIdx + 2 });
			if (worksheet[addr]) worksheet[addr].z = UNIT_FORMATS[row.unit];
		});
	});

	worksheet["!cols"] = header.map((label, cIdx) => {
		const longest =
			cIdx === 0
				? Math.max(0, ...rows.map((r) => r.group.length))
				: cIdx === 1
					? Math.max(0, ...rows.map((r) => r.metric.length))
					: 0;
		return { wch: Math.max(label.length, longest, 10) };
	});

	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, SHEET_NAME);
	return workbook;
}

/** Build the workbook for the loaded table and hand it to the browser. */
export function downloadKpiTable(table: KpiTable, today = new Date()): void {
	XLSX.writeFile(buildKpiWorkbook(table), kpiSpreadsheetFilename(table, today));
}
